import { useCallback, useMemo } from 'react';
import { useBoolean } from 'usehooks-ts';
import {
  useRetrieveWorkspaceBooks,
  useRetrieveAllWorkspaceBookConnections,
} from '@/generated/hooks/bdk/v2/services/library';
import {
  useUserPreferencesStore,
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
} from '@/shared/stores';
import { FilterBuilder } from '@/shared/utils';
import {
  filterConnectionsByStage,
  getLatestVersionIntegrations,
} from '@/modules/integrations';
import type { Bdkv2BookDescriptor } from '@/generated/hooks/bdk/models';
import { BOOK_CATALOG_STALE_TIME } from '@/modules/integrations/constants';
import type { IUseTriggerConfigureDataParams } from './types';

/**
 * Shared data logic for trigger dialog configure steps.
 *
 * Fetches the integration list and connections, derives the unique integration
 * list and draft connections, resolves the selected integration's versioned
 * descriptor, and exposes the AddConnectionDialog open/close state plus a
 * success handler. The org/workspace ids and latest integration version are
 * returned so callers can wire up `useConnectionActions` for the dialog.
 *
 * This is identical across poll and webhook triggers; only the books filter
 * field and excluded app names differ.
 *
 * @param params - Hook params (selection state, callbacks, books filter)
 * @returns Integration/connection data, loading flags, handlers, and dialog state
 */
export function useTriggerConfigureData({
  selectedIntegration,
  selectedConnection,
  onConnectionChange,
  onIntegrationVersionChange,
  booksFilterField,
  excludedAppNames,
}: IUseTriggerConfigureDataParams) {
  // Dialog state for AddConnectionDialog
  const {
    value: isAddConnectionDialogOpen,
    setTrue: openAddConnectionDialog,
    setFalse: closeAddConnectionDialog,
  } = useBoolean(false);

  // Get organization and workspace IDs
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  // Fetch all books that match the requested capability (polls or triggers)
  const { data: booksData, isLoading: isLoadingIntegrations } =
    useRetrieveWorkspaceBooks(
      organizationId,
      workspaceId,
      {
        page_size: 100,
        filter: FilterBuilder.create().equals(booksFilterField, true).build(),
      },
      {
        query: {
          enabled: !!organizationId && !!workspaceId,
          staleTime: BOOK_CATALOG_STALE_TIME,
        },
      },
    );

  // Optionally filter out integrations that have dedicated trigger dialogs
  // (e.g. email) and de-duplicate by integration name (one entry per unique
  // integration, latest version).
  const { uniqueIntegrations, integrationVersionsMap } = useMemo(() => {
    const allIntegrations = booksData?.data?.books ?? [];
    const filteredIntegrations = excludedAppNames?.length
      ? allIntegrations.filter(
          integration =>
            !excludedAppNames.some(
              excluded =>
                integration.name?.toLowerCase() === excluded.toLowerCase(),
            ),
        )
      : allIntegrations;

    // Group all versions by integration name
    const versionsMap = new Map<string, Bdkv2BookDescriptor[]>();
    filteredIntegrations.forEach(integration => {
      const name = integration.name ?? '';
      if (!versionsMap.has(name)) {
        versionsMap.set(name, []);
      }
      versionsMap.get(name)!.push(integration);
    });

    // Get unique integrations (latest version of each integration)
    const unique = getLatestVersionIntegrations(filteredIntegrations);

    return { uniqueIntegrations: unique, integrationVersionsMap: versionsMap };
  }, [booksData?.data?.books, excludedAppNames]);

  // Get all versions of the selected integration
  const selectedIntegrationVersions = useMemo(
    () =>
      selectedIntegration
        ? (integrationVersionsMap.get(selectedIntegration) ?? [])
        : [],
    [selectedIntegration, integrationVersionsMap],
  );

  // Fetch ALL connections for the selected integration (across all versions)
  const {
    data: connectionsData,
    isLoading: isLoadingConnections,
    refetch: refetchConnections,
  } = useRetrieveAllWorkspaceBookConnections(
    organizationId,
    workspaceId,
    {
      page_size: 100,
      filter: selectedIntegration
        ? FilterBuilder.create()
            .equals('book_name', selectedIntegration)
            .build()
        : undefined,
    },
    {
      query: {
        enabled: Boolean(organizationId && workspaceId && selectedIntegration),
      },
    },
  );

  // Get available version strings from the grouped integration
  const availableVersions = useMemo(
    () =>
      new Set(
        selectedIntegrationVersions.map(integration => integration.version),
      ),
    [selectedIntegrationVersions],
  );

  // Filter to only show draft connections with versions available in the grouped integration
  const draftConnections = useMemo(() => {
    const allConnections = connectionsData?.data?.book_connections ?? [];
    // First filter by available versions
    const versionFiltered = allConnections.filter(
      conn => conn.book_version && availableVersions.has(conn.book_version),
    );
    // Then filter to only get draft connections
    return filterConnectionsByStage(versionFiltered, 'draft');
  }, [connectionsData?.data?.book_connections, availableVersions]);

  // Find the selected integration's data based on the selected connection's book_version
  // This is needed for getting polls/triggers for the specific version
  const selectedIntegrationData = useMemo(() => {
    if (!selectedIntegration) return undefined;
    const versions = integrationVersionsMap.get(selectedIntegration) ?? [];

    // If a connection is selected, find the version matching that connection's book_version
    if (selectedConnection) {
      const selectedConn = draftConnections.find(
        c => c.connection_id === selectedConnection,
      );
      if (selectedConn?.book_version) {
        const versionedIntegration = versions.find(
          integration => integration.version === selectedConn.book_version,
        );
        if (versionedIntegration) return versionedIntegration;
      }
    }

    // Default to first version
    return versions[0];
  }, [
    selectedIntegration,
    integrationVersionsMap,
    selectedConnection,
    draftConnections,
  ]);

  /**
   * Handles connection selection change.
   * Updates the integration version based on the selected connection's book_version.
   */
  const handleConnectionChange = useCallback(
    (connectionId: string) => {
      onConnectionChange?.(connectionId);

      // Find the selected connection and update integration version
      const selectedConn = draftConnections.find(
        c => c.connection_id === connectionId,
      );
      if (selectedConn?.book_version) {
        onIntegrationVersionChange?.(selectedConn.book_version);
      }
    },
    [draftConnections, onConnectionChange, onIntegrationVersionChange],
  );

  // Get the latest version of the selected integration for creating new
  // connections. `uniqueIntegrations` is already semver-sorted by
  // getLatestVersionIntegrations, unlike selectedIntegrationVersions which
  // preserves API insertion order.
  const latestIntegrationVersion =
    uniqueIntegrations.find(
      integration => integration.name === selectedIntegration,
    )?.version ?? '';

  /**
   * Handle successful connection creation: refetch and auto-select the new one.
   */
  const handleConnectionSuccess = useCallback(async () => {
    closeAddConnectionDialog();
    // Refetch connections to get the newly created one
    const { data } = await refetchConnections();
    const newConnections = data?.data?.book_connections ?? [];
    // Auto-select the first connection if one was created
    if (newConnections.length > 0) {
      const newConnection = newConnections[0];
      onConnectionChange?.(newConnection.connection_id ?? '');
      // Update integration version based on the new connection
      if (newConnection.book_version) {
        onIntegrationVersionChange?.(newConnection.book_version);
      }
    }
  }, [
    closeAddConnectionDialog,
    refetchConnections,
    onConnectionChange,
    onIntegrationVersionChange,
  ]);

  return {
    organizationId,
    workspaceId,
    uniqueIntegrations,
    isLoadingIntegrations,
    isLoadingConnections,
    draftConnections,
    selectedIntegrationData,
    handleConnectionChange,
    isAddConnectionDialogOpen,
    openAddConnectionDialog,
    closeAddConnectionDialog,
    handleConnectionSuccess,
    latestIntegrationVersion,
  };
}
